<template>
  <div>
    <v-container>
      <div class="back-link" @click="goBackToBillingInfo">
        <img src="@/assets/images/icons/arrow-left.svg" alt="Back" class="back-icon" />
        <span>Return to Billing Information</span>
      </div>
      <v-card>
        <v-card-text>
          <!-- Manual subscription message -->
          <div v-if="isManualTier" class="manual-sub-view">
            <h2>Manual Subscription Required</h2>
            <p>
              Your <strong>{{ selectedTierName }}</strong> subscription requires some manual configuration. By clicking "Subscribe", your account will be set to "Pending" and a Structure Developer Specialist will reach out to you if they require any further information.
            </p>
            <v-btn :disabled="isLoading" color="primary" large class="mt-4 str-btn-primary" @click="handleManualSubscription">
              <v-progress-circular v-if="isLoading" indeterminate size="24"></v-progress-circular>
              <span v-else>Subscribe</span>
            </v-btn>
          </div>

          <!-- Stripe form for automatic subscriptions -->
          <div v-else>
            <!-- Loading state with better visual feedback -->
            <div v-if="!stripeLoaded && !errorMessage" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-4 text-gray-500">Loading Secure Payment Form...</p>
            </div>

            <!-- The Embedded Checkout form will be mounted here -->
            <div id="checkout"></div>
          </div>

          <div v-if="errorMessage" class="error-message mt-4 text-red-600 font-semibold border border-red-200 p-3 rounded bg-red-50">
            Error: {{ errorMessage }}
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { loadStripe } from '@stripe/stripe-js';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import axios from 'axios';

const store = useStore();
const router = useRouter();
const { getAccessTokenSilently, user } = useAuth0();

const selectedTier = computed(() => store.state.selectedTier);
const customerType = computed(() => store.state.authentication.customer_type);
const isCaptureOnly = computed(() => customerType.value === 'capture');
const isManualTier = computed(() => {
  return selectedTier.value && selectedTier.value.tier === 'Enterprise';
});
const selectedTierName = computed(() => selectedTier.value?.tier || '');
const isLoading = ref(false);

// Assumed environment variables are correctly loaded
const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
const stripe = ref(null);
const stripeLoaded = ref(false);
const errorMessage = ref(null);
const isInitializing = ref(false); // NEW: Flag to prevent concurrent initialization
const checkoutRef = ref(null); // NEW: To store the checkout object

const goBackToBillingInfo = () => {
  router.push({ name: 'BillingInfo' });
};

const handleManualSubscription = async () => {
  isLoading.value = true;
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };
    const userId = user.value.sub;
    const userEmail = user.value.email;
    const tierName = selectedTierName.value;

    if (!tierName) {
      throw new Error("Could not determine subscription tier name.");
    }

    // Fetch all tiers to find the ID for the selected tier name
    const tiersResponse = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/tiers`, { headers });
    const selectedTierObject = tiersResponse.data.find(t => t.name === tierName);

    if (!selectedTierObject) {
      throw new Error(`Could not find details for tier: ${tierName}`);
    }
    const tierId = selectedTierObject.id; // Use the 'id' property

    const updateUserTierPromise = axios.patch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/users/${userId}`, {
      tier: tierId // Send the tier ID to the user endpoint
    }, { headers });

    const updateOnboardingPromise = axios.patch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/users/onboardings/${userId}`, {
      has_set_billing: true // Send the billing status to the onboarding endpoint
    }, { headers });

    const sendNotificationPromise = axios.post(`${process.env.VUE_APP_CBE_BASE_URL}/helpscout/registration/manual`, {
      email: userEmail,
      tier: tierName, // Send the tier name to HelpScout
      auth0_id: userId
    }, { headers });

    await Promise.all([updateUserTierPromise, updateOnboardingPromise, sendNotificationPromise]);

    // Clear billing info from store
    store.commit('CLEAR_BILLING_INFO_FORM');

    // Update billing status in store before navigating
    const authData = store.state.authentication;
    store.commit('SET_AUTHENTICATION', {
        ...authData,
        hasBillingInfo: true,
    });

    // On success, navigate to the account page
    router.push({ name: 'AccountView' });

  } catch (error) {
    console.error("Failed to update subscription:", error);
    const userMessage = error.response?.data?.error || "Failed to update subscription. Please try again.";
    errorMessage.value = userMessage;
  } finally {
    isLoading.value = false;
  }
};

async function fetchCapturePriceId(headers) {
  const tiersResponse = await axios.get(
    `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/tiers`,
    { headers }
  );
  const captureTier = tiersResponse.data.find(t => t.name === 'Structure Capture Enterprise');
  return captureTier?.stripe_price_id ?? null;
}

async function buildSubscriptionPayload(headers) {
  const payload = {};

  if (!isCaptureOnly.value) {
    if (!selectedTier.value?.stripePriceId) {
      throw new Error('Could not find a price for the selected tier. Please try again.');
    }
    payload.price_id = selectedTier.value.stripePriceId;
    if (selectedTier.value.stripeMeteredPriceId) {
      payload.metered_price_id = selectedTier.value.stripeMeteredPriceId;
    }
  }

  if (isCaptureOnly.value || customerType.value === 'both') {
    const capturePriceId = await fetchCapturePriceId(headers);
    if (!capturePriceId) {
      throw new Error('Could not find the Capture Enterprise price. Please contact support.');
    }
    payload.capture_price_id = capturePriceId;
  }

  return payload;
}

onMounted(async () => {
  if (isManualTier.value) {
    stripeLoaded.value = true;
    return;
  }

  // Guard: prevent concurrent or duplicate initialization
  if (isInitializing.value || stripeLoaded.value) {
    console.warn("Stripe checkout initialization is already running or complete.");
    return;
  }
  isInitializing.value = true;

  try {
    // 1. Load Stripe
    stripe.value = await loadStripe(publishableKey);

    if (stripe.value) {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      if (!isCaptureOnly.value && !selectedTier.value) {
        errorMessage.value = "No subscription tier selected. Please go back and select a tier.";
        return;
      }

      // 2. Build payload and call backend to create the Embedded Checkout Session
      const subscriptionPayload = await buildSubscriptionPayload(headers);
      const response = await axios.post(`${process.env.VUE_APP_CBE_BASE_URL}/stripe/create-subscription`, subscriptionPayload, { headers });

      const { clientSecret } = response.data;

      if (!clientSecret) {
        errorMessage.value = "Backend did not return a valid client secret.";
        return;
      }

      // 3. Use initEmbeddedCheckout() with the Checkout Session secret and AWAIT the result
      const checkout = await stripe.value.initEmbeddedCheckout({
        clientSecret: clientSecret,
      });
      checkoutRef.value = checkout; // Store the checkout object

      // 4. Mount the entire Embedded Checkout component into the target div
      checkout.mount('#checkout');

      stripeLoaded.value = true;
    }
  } catch (error) {
    console.error("Failed to load Stripe or initialize checkout:", error);
    const userMessage =
      error.response?.data?.error ||
      error.message ||
      "Failed to load payment form. Please try again.";
    errorMessage.value = userMessage;
  } finally {
    // Ensure the flag is always reset, even on error
    isInitializing.value = false;
  }
});

// NEW: onUnmounted hook to destroy the checkout instance
onUnmounted(() => {
  if (checkoutRef.value) {
    checkoutRef.value.destroy();
    checkoutRef.value = null; // Clear the ref
    console.log("Stripe Embedded Checkout instance destroyed.");
  }
});

// NOTE: The manual handleSubmit function is removed because initEmbeddedCheckout
// creates a full, self-contained payment form that handles its own submission and redirection.
</script>


<style scoped lang="scss">
    @import '@/assets/styles/StrButtons.scss'
    @import '@/assets/styles/CheckoutView.scss'
</style>